import { create } from "zustand";
import {
  AMOY_CHAIN_ID,
  POLYGON_CHAIN_ID,
  SUPPORTED_CHAINS,
  STORED_CHAIN_KEY,
  getActiveChainId,
  isSupportedChainId,
  storeActiveChainId,
} from "@/config/activeChain";

export { AMOY_CHAIN_ID, POLYGON_CHAIN_ID };

/** Render straight from the canonical chain list -- see config/activeChain.ts. */
export const CHAIN_OPTIONS = SUPPORTED_CHAINS;

interface NetworkStore {
  /** The chain this page load is running against. Never changes in-place. */
  chainId: number;
  /** Set when another tab switched chains; this tab is still on the old one. */
  changedElsewhere: number | null;
  /**
   * Persists the choice and reloads so every module re-initialises on it.
   * Returns false when the choice could not be stored, in which case nothing
   * happened and the caller should tell the user.
   */
  switchChain: (chainId: number) => boolean;
  /** Adopt a chain another tab selected (reloads, same as switching here). */
  reloadOntoStoredChain: () => void;
}

/**
 * The app's active chain, as chosen by an admin in the top-bar switch.
 *
 * Reading it here is a convenience for components -- activeChain.ts is the
 * source of truth, and non-React code (getChainConfig, the Privy config)
 * reads that directly.
 *
 * `switchChain` reloads on purpose. The Privy provider picks its chain once
 * at mount, PolygonClient caches contract addresses, and react-query holds
 * per-chain rows; a reload is the one cheap way to move all of them at once
 * instead of leaving the app half on Amoy and half on Polygon.
 */
export const useNetworkStore = create<NetworkStore>(() => ({
  chainId: getActiveChainId(),
  changedElsewhere: null,

  switchChain: (chainId) => {
    if (!isSupportedChainId(chainId) || chainId === getActiveChainId()) return false;
    // Reload only if the choice actually persisted. A swallowed write (quota,
    // locked-down storage) would otherwise reload straight back onto the old
    // chain and look like the switch silently did nothing.
    if (!storeActiveChainId(chainId)) return false;
    // Deliberately no set({ chainId }) here. reload() schedules a navigation
    // rather than halting synchronously, so flipping the store first lets
    // subscribers (AdminDashboard's chain-scoped queries) re-render and refetch
    // on the new chain while getChainConfig()/PolygonClient/Privy are still
    // frozen on the old one -- the split-brain this reload exists to avoid.
    // The store stays truthful about the chain this page load is actually on.
    window.location.reload();
    return true;
  },

  reloadOntoStoredChain: () => {
    window.location.reload();
  },
}));

/**
 * Another tab switching chains writes the same localStorage key, but this tab
 * stays frozen on the chain it loaded with. Without this it would keep showing
 * the old network as active and then silently adopt the new one on its next
 * reload -- including onto mainnet, with no confirmation. Surface it instead
 * and let the user reload deliberately.
 */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== STORED_CHAIN_KEY) return;
    const next = Number(event.newValue);
    if (!isSupportedChainId(next) || next === getActiveChainId()) {
      useNetworkStore.setState({ changedElsewhere: null });
      return;
    }
    useNetworkStore.setState({ changedElsewhere: next });
  });
}
